import React from 'react';
import useDataSourceContext from '../../DataSource/useDataSourceContext';
import styled from 'styled-components';
import useIntl from '@afosto/utils-react/intl/useIntl';
import { useTheme } from '@material-ui/core/styles';
import IconButton from '@material-ui/core/IconButton';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import { FormattedMessage } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import SvgIcon from '@material-ui/core/SvgIcon';
import translations from '../translations';

const DataListPaginationContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-shrink: 0;
  margin-top: ${props => props.theme.spacing(2)}px;

  @media (min-width: ${props => props.theme.breakpoints.values.md}px) {
    margin-left: ${props => props.theme.spacing(2.5)}px;
  }
`;

const DataListPagination = () => {
  const intl = useIntl();
  const { data, pagination = {} } = useDataSourceContext();
  const { activePage, goToPage, itemCount, itemsPerPage = 10, getNextPageCursor } = pagination;
  const theme = useTheme();
  const nextPageCursor = getNextPageCursor();
  const count = itemCount ? parseInt(itemCount, 10) : data.length;

  const displayedItemsLabel = () => {
    const from = count === 0 ? 0 : activePage * itemsPerPage + 1;
    const to =
      count !== -1
        ? Math.min(count, (activePage + 1) * itemsPerPage)
        : (activePage + 1) * itemsPerPage;
    const totalCount = count === -1 ? -1 : count;

    let toLabel = itemCount !== undefined ? to : to + from - 1;
    let countLabel = itemCount;

    if (toLabel < 0) {
      toLabel = 0;
    }

    if (!itemCount && totalCount < itemsPerPage) {
      countLabel = toLabel;
    } else if (!itemCount) {
      countLabel = intl.formatMessage({
        id: 'dataList.pagination.many',
        defaultMessage: 'many',
        description: 'Label when the total number of rows are unknown',
      });
    }

    return intl.formatMessage(
      {
        id: 'dataList.pagination.displayedRows',
        defaultMessage: '{from}-{to} of {count}',
        description: 'Data list displayed rows label',
      },
      {
        from,
        to: toLabel,
        count: countLabel,
      },
    );
  };

  /**
   * Handle the change page event. Sets the active page.
   * @param {Object} event
   * @param {number} page
   */
  const handleChangePage = (event, page) => {
    goToPage(page);
  };

  /**
   * Handle the click on the previous page button.
   * @param {Event} event
   */
  const handlePreviousButtonClick = event => {
    handleChangePage(event, activePage - 1);
  };

  /**
   * Handle the click on the next page button
   * @param {Event} event
   */
  const handleNextButtonClick = event => {
    handleChangePage(event, activePage + 1);
  };

  const hasNoCount = itemCount === undefined && nextPageCursor === undefined;
  const nextPageDisabled =
    itemCount !== undefined ? activePage >= Math.ceil(count / itemsPerPage) - 1 : hasNoCount;

  return (
    <DataListPaginationContainer>
      <IconButton
        onClick={handlePreviousButtonClick}
        disabled={activePage === 0}
        aria-label={intl.formatMessage(translations.pagination.previousPageLabel)}
        color="inherit"
      >
        {theme.direction === 'rtl' ? (
          <SvgIcon>
            <FontAwesomeIcon fixedWidth icon={['fal', 'angle-right']} />
          </SvgIcon>
        ) : (
          <SvgIcon>
            <FontAwesomeIcon fixedWidth icon={['fal', 'angle-left']} />
          </SvgIcon>
        )}
      </IconButton>
      <Box display="flex" flexDirection="column" alignItems="center">
        <FormattedMessage
          {...translations.pagination.currentPageLabel}
          values={{ page: activePage + 1 }}
        />{' '}
        <Typography variant="caption" color="textSecondary">
          {displayedItemsLabel()}
        </Typography>
      </Box>
      <IconButton
        onClick={handleNextButtonClick}
        disabled={nextPageDisabled}
        aria-label={intl.formatMessage(translations.pagination.nextPageLabel)}
        color="inherit"
      >
        {theme.direction === 'rtl' ? (
          <SvgIcon>
            <FontAwesomeIcon fixedWidth icon={['fal', 'angle-left']} />
          </SvgIcon>
        ) : (
          <SvgIcon>
            <FontAwesomeIcon fixedWidth icon={['fal', 'angle-right']} />
          </SvgIcon>
        )}
      </IconButton>
    </DataListPaginationContainer>
  );
};

export default DataListPagination;
